import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/lib/Seo";
import { api, apiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Contact() {
  const { settings } = useSite();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Message envoyé !", { description: "Nous vous répondons au plus vite." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(apiError(err, "Envoi impossible, réessayez."));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo title="Contact" path="/contact"
        description="Contactez l'équipe de Douzième Homme — Le Jeu : question sur le jeu, commande, presse ou partenariat. Réponse rapide depuis Nice." />
      <section className="py-14 sm:py-20" data-testid="contact-page">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-4">Contact</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight uppercase leading-[1.05] text-onyx">
              Écrivez-nous.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Une question sur le jeu, une commande, la presse ou un partenariat ? L'équipe vous répond depuis Nice.
            </p>
            <ul className="mt-9 space-y-4 text-sm sm:text-base">
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-rouge shrink-0" />
                <a href={`mailto:${settings?.contact_email}`} data-testid="contact-email" className="font-semibold text-onyx hover:text-rouge transition-colors break-all">
                  {settings?.contact_email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-rouge shrink-0" />
                <a href={`tel:${(settings?.phone || "").replace(/\s/g, "")}`} data-testid="contact-phone" className="font-semibold text-onyx hover:text-rouge transition-colors">
                  {settings?.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-rouge shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{settings?.address}</span>
              </li>
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <form onSubmit={submit} className="bg-white border border-line p-6 sm:p-9 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-name">Nom *</Label>
                  <Input id="contact-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="contact-email-input">Email *</Label>
                  <Input id="contact-email-input" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label htmlFor="contact-subject">Sujet</Label>
                <Input id="contact-subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")} className="mt-1.5" placeholder="Question sur le jeu, commande, presse…" />
              </div>
              <div>
                <Label htmlFor="contact-message">Message *</Label>
                <Textarea id="contact-message" rows={6} data-testid="contact-message" required value={form.message} onChange={set("message")} className="mt-1.5" />
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-rouge text-white px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
